import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchHomePageDoctors,
  fetchDoctorsList,
  addHomePageDoctor,
  editHomePageDoctor,
  updateHomePageDoctor,
  trashHomePageDoctor,
} from "../../Adapters/ApiAdapter";

const HomePageDoctors = () => {
  const [allDoctors, setAllDoctors] = useState([]);
  const [homepageDoctors, setHomepageDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allRes, homepageRes] = await Promise.all([
        fetchDoctorsList(),
        fetchHomePageDoctors(),
      ]);

      const all = (allRes.data || []).map((doc) => ({
        id: doc.id,
        name: doc.name,
      }));

      setAllDoctors(all);
      setFilteredDoctors(all);
      setHomepageDoctors(homepageRes.data || []);
    } catch {
      toast.error("Failed to fetch doctors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let data = [...allDoctors];

    if (search.trim()) {
      data = data.filter((d) =>
        d.name.toLowerCase().includes(search.trim().toLowerCase())
      );
    }

    if (sortField === "name") {
      data.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortField === "id") {
      data.sort((a, b) => a.id - b.id);
    }

    setFilteredDoctors(data);
  }, [search, sortField, allDoctors]);

  const homepageDoctorIds = homepageDoctors.map((d) => d.doctor_id);

  // const handleAddDoctor = async (doctor_id) => {
  //   if (homepageDoctors.length >= 4) {
  //     toast.warning("Only 4 doctors can be added.");
  //     return;
  //   }

  //   const display_order = homepageDoctors.length + 1;
  //   const formData = { doctor_id, display_order };

  //   setLoadingAction(true);
  //   try {
  //     await addHomePageDoctor(formData);
  //     toast.success("Doctor added to homepage.");
  //     fetchData();
  //   } catch {
  //     toast.error("Failed to add doctor.");
  //   } finally {
  //     setLoadingAction(false);
  //   }
  // };

  const handleAddDoctor = async (doctor_id) => {
    const existingOrders = homepageDoctors.map((doc) => Number(doc.display_order));
    let display_order = 1;
    while (existingOrders.includes(display_order) && display_order <= 4) {
      display_order++;
    }

    if (display_order > 4) {
      toast.warning("Only 4 doctors can be added to homepage.");
      return;
    }

    const formData = { doctor_id, display_order };

    setLoadingAction(true);
    try {
      await addHomePageDoctor(formData);
      toast.success("Doctor added to homepage.");
      fetchData();
    } catch (err) {
      toast.error("Failed to add doctor.");
    } finally {
      setLoadingAction(false);
    }
  };


  const handleEdit = async (entryId) => {
    try {
      const res = await editHomePageDoctor(entryId);
      setEditItem(res.data);
    } catch {
      toast.error("Failed to load doctor.");
    }
  };

  const handleUpdate = async () => {
    setLoadingAction(true);
    try {
      await updateHomePageDoctor(editItem.homepage_entry_id, {
        doctor_id: editItem.doctor_id,
        display_order: editItem.display_order,
      });
      toast.success("Doctor updated.");
      setEditItem(null);
      fetchData();
    } catch {
      toast.error("Update failed.");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleTrash = async (entryId) => {
    try {
      await trashHomePageDoctor(entryId);
      toast.success("Doctor removed.");
      fetchData();
    } catch {
      toast.error("Failed to remove doctor.");
    }
  };

  const allDoctorColumns = [
    { field: "id", headerName: "Doctor ID", width: 100 },
    { field: "name", headerName: "Doctor Name", flex: 1 },
    {
      field: "actions",
      headerName: "Add to Homepage",
      width: 180,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          disabled={homepageDoctorIds.includes(params.row.id) || homepageDoctors.length >= 4}
          onClick={() => handleAddDoctor(params.row.id)}
        >
          Add
        </Button>
      ),
    },
  ];

  const homepageColumns = [
    { field: "homepage_entry_id", headerName: "ID", width: 80 },
    { field: "doctor_name", headerName: "Name", flex: 1 },
    { field: "display_order", headerName: "Order", width: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleEdit(params.row.homepage_entry_id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            sx={{ ml: 1 }}
            onClick={() => handleTrash(params.row.homepage_entry_id)}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m={2}>
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Homepage Doctors Management
      </Typography>

      {/* Filter and sort */}
      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Search Doctor"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortField}
            label="Sort By"
            onChange={(e) => setSortField(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="id">ID</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Typography variant="h6" gutterBottom>
        All Doctors
      </Typography>
      <DataGrid
        rows={filteredDoctors}
        columns={allDoctorColumns}
        getRowId={(row) => row.id}
        autoHeight
        loading={loading}
      />

      <Typography variant="h6" sx={{ mt: 5, mb: 1 }}>
        Selected Homepage Doctors (Max 4)
      </Typography>
      <DataGrid
        rows={homepageDoctors}
        columns={homepageColumns}
        getRowId={(row) => row.homepage_entry_id}
        autoHeight
        loading={loading}
      />

      {/* Edit Dialog */}
      {editItem && (
        <Dialog open onClose={() => setEditItem(null)}>
          <DialogTitle>Edit Homepage Doctor</DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="Doctor ID"
              value={editItem.doctor_id}
              onChange={(e) =>
                setEditItem({ ...editItem, doctor_id: e.target.value })
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Display Order"
              value={editItem.display_order}
              onChange={(e) =>
                setEditItem({ ...editItem, display_order: e.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditItem(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdate} disabled={loadingAction}>
              {loadingAction ? <CircularProgress size={24} /> : "Update"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default HomePageDoctors;
